/**
 * rb-intake.js — how a resume begins: blank, from an example, or from the
 * person's old resume (file or pasted text).
 *
 * A blank form loses people (competitor research 2026-10-09; Avito's pre-filled
 * resume lifted published resumes by 26 %), and "образец резюме" alone is
 * ~83 k searches a month — so examples by profession are a first-class start.
 *
 * Import: the text comes out of the file in the browser (PDF via pdf.js, Word
 * via mammoth, hh.ru's RTF by a small reader, TXT, our own JSON), then the
 * assistant lays it into fields ('parse' — its own daily bucket, contacts
 * leave as placeholders). Without the assistant, or when it refuses, the local
 * heuristic parser (rb-import.js) does a rougher job instead of nothing.
 * An import always becomes a NEW resume: the old tool replaced the current
 * draft without asking (audit 2026-10-09).
 *
 * globalThis.RbIntake
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    var cx = null;

    function init(context) { cx = context; }

    function sample(id) {
        var data = globalThis.__rbSamples || {};
        if (data.first && data.first.id === id) return data.first;
        return (data.professions || []).filter(function (p) { return p.id === id; })[0] || null;
    }

    /* start-screen actions: 'blank' | 'paste' | 'sample:<id>' */
    function act(a) {
        if (a === 'blank') { cx.newWork(null, { pane: 'personal', why: 'blank' }); return; }
        if (a === 'paste') { openPaste(); return; }
        var m = /^sample:(.+)$/.exec(a || '');
        if (m) {
            var s = sample(m[1]);
            if (!s) return;
            var model = JSON.parse(JSON.stringify(s.model));
            model.meta = Object.assign({}, model.meta || {}, { sample: s.id });
            cx.newWork(model, { pane: 'personal', why: 'sample', sample: s.id });
        }
    }

    /* ---- paste --------------------------------------------------------- */
    function openPaste() {
        var dlg = document.getElementById('rbPasteDlg');
        var ta = document.getElementById('rbPasteText');
        if (!dlg) return;
        ta.value = '';
        var d = dlg.querySelector('[data-ai-disclose]');
        if (d) d.innerHTML = globalThis.RbAi.live() ? globalThis.RbAi.disclosure() : '';
        if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
        ta.focus();
        cx.track('import_open', { via: 'paste' });
    }
    function bindPaste() {
        var go = document.getElementById('rbPasteGo');
        if (!go) return;
        go.addEventListener('click', function () {
            var ta = document.getElementById('rbPasteText');
            var txt = (ta.value || '').trim();
            if (txt.length < 120) { toast(T('import_paste_empty'), 'warning'); ta.focus(); return; }
            var dlg = document.getElementById('rbPasteDlg');
            if (dlg.close) dlg.close(); else dlg.removeAttribute('open');
            fromText(txt, 'paste');
        });
    }

    /* ---- file ---------------------------------------------------------- */
    function bindFile() {
        var zone = document.getElementById('rbImportDrop');
        if (!zone) return;
        /* __bindUploaderFile calls back with (file, detail) — the first argument is
           the File itself; reading it as the event detail dropped every import */
        var take = function (file, detail) {
            var f = file || (detail && (detail.file || (detail.files && detail.files[0])));
            if (f) fromFile(f);
        };
        if (window.__bindUploaderFile) window.__bindUploaderFile(zone, take);
        else zone.addEventListener('tool-uploader:file', function (e) { take(null, e.detail); });
    }

    function ext(name) { return String(name || '').toLowerCase().split('.').pop(); }

    function fromFile(file) {
        var e = ext(file.name);
        cx.track('import_open', { via: 'file', ext: e });
        cx.busy(T('import_reading'));
        readText(file, e).then(function (res) {
            if (res.model) { cx.busy(null); cx.newWork(res.model, { pane: 'personal', why: 'json' }); return; }
            var txt = String(res.text || '').replace(/\u0000/g, '').trim();
            if (txt.length < 120) {
                cx.busy(null);
                cx.notice(e === 'pdf' ? T('import_scanned', { ocr: '' }) : T('import_no_text'), e === 'pdf' ? { href: cx.cfg().ocr, label: T('import_ocr_link') } : null);
                cx.track('import_empty', { ext: e });
                return;
            }
            fromText(txt, 'file:' + e);
        }).catch(function (err) {
            cx.busy(null);
            if (window.__handleError) window.__handleError(err, 'resume_import_read', { say: T('import_read_failed') });
            else toast(T('import_read_failed'), 'error');
        });
    }

    function readText(file, e) {
        if (e === 'json') {
            return file.text().then(function (s) {
                var j = JSON.parse(s);
                if (!globalThis.RbState.looksLikeResume(j)) throw new Error('not a resume json');
                return { model: j };
            });
        }
        if (e === 'txt' || e === 'rtf') return docTextOf(file);
        if (e === 'docx') {
            return globalThis.RbExportText.loadVendor('mammoth').then(function () {
                return file.arrayBuffer();
            }).then(function (buf) {
                return window.mammoth.extractRawText({ arrayBuffer: buf });
            }).then(function (r) { return { text: r.value }; });
        }
        if (e === 'pdf') return pdfText(file).then(function (s) { return { text: s }; });
        return file.text().then(function (s) { return { text: s }; });
    }

    function pdfText(file) {
        /* the door waits for a late loader instead of failing at once */
        var load = window.__pdfjs(window.__rbPdfjs || []);
        return Promise.all([load, file.arrayBuffer()]).then(function (r) {
            var lib = r[0];
            return lib.getDocument({ data: new Uint8Array(r[1]) }).promise;
        }).then(function (pdf) {
            var pages = [];
            var n = Math.min(pdf.numPages, 8);
            for (var i = 1; i <= n; i++) pages.push(pdf.getPage(i).then(function (pg) { return pg.getTextContent(); }));
            return Promise.all(pages).then(function (contents) {
                return contents.map(function (c) {
                    var out = '', lastY = null;
                    c.items.forEach(function (it) {
                        var y = it.transform ? it.transform[5] : null;
                        if (lastY !== null && y !== null && Math.abs(y - lastY) > 2 && !/\n$/.test(out)) out += '\n';
                        out += it.str + (it.hasEOL ? '\n' : (it.str && !/\s$/.test(it.str) ? ' ' : ''));
                        lastY = y;
                    });
                    return out;
                }).join('\n\n');
            });
        });
    }

    /* .txt and .rtf come through the site's document door, public/js/document-text.js:
       every codepage a Windows or DOS machine writes, and the one RTF reader. The
       reader that lived here kept Word's font table as text («Times New Roman;
       Arial;…», measured 28.10.2026 on tests/fixtures/document-text/word-letter.rtf)
       and read a cp1251 .txt as U+FFFD replacement marks; an hh.ru RTF without a declared
       codepage is pinned in tests/js/document-text.test.mjs. */
    function docTextOf(file) {
        var url = globalThis.__rbCfg && globalThis.__rbCfg.docText;
        if (!url) return file.text().then(function (s) { return { text: s }; });
        return import(url).then(function (m) { return m.docText(file); }).then(function (r) { return { text: r.text }; });
    }

    /* ---- text → model -------------------------------------------------- */
    function fromText(txt, via) {
        var local = function (why) {
            var region = cx.model() ? cx.model().meta.region : null;
            try {
                var parsed = globalThis.RbImport.parse(txt, region || 'us');
                cx.busy(null);
                cx.newWork(parsed, { pane: 'personal', why: 'import_local', notice: T('import_local_done') });
                cx.track('import_done', { via: via, engine: 'local', why: why });
            } catch (e) {
                cx.busy(null);
                toast(T('import_read_failed'), 'error');
            }
        };
        if (!globalThis.RbAi.live() || globalThis.RbAi.left('import') === 0) { local('no_ai'); return; }
        cx.busy(T('import_ai_working'), true);
        globalThis.RbAi.run('parse', { text: txt.slice(0, 24000) }, { onWait: function (w) { cx.busyProgress(w); } }).then(function (res) {
            cx.busy(null);
            cx.newWork(toModel(res), { pane: 'personal', why: 'import_ai', notice: T('import_ai_done') });
            cx.track('import_done', { via: via, engine: 'ai' });
        }, function (e) {
            if (e && e.kind === 'limit') toast(e.message, 'info');
            local(e && e.kind || 'ai_failed');
        });
    }

    /* the assistant's fields → a ResumeModel */
    function toModel(r) {
        var S = globalThis.RbState;
        var m = S.createFreshState(cx.defaultRegion(), '');
        var p = r.personal || {};
        ['name', 'title', 'email', 'phone', 'city', 'website', 'linkedin', 'github'].forEach(function (k) { if (p[k]) m.personal[k] = p[k]; });
        if (p.dob && /^\d{4}-\d{2}-\d{2}$/.test(p.dob)) m.personal.dob = p.dob;
        m.summary = r.summary || '';
        var put = function (key, rows, map) {
            (rows || []).forEach(function (x) {
                var e = S.newEntry(key);
                Object.keys(e).forEach(function (f) { if (f !== 'id' && x[f] != null && x[f] !== '') e[f] = typeof e[f] === 'boolean' ? !!x[f] : String(x[f]); });
                if (map) map(e, x);
                m[key].push(e);
            });
        };
        put('experience', r.experience);
        put('education', r.education);
        put('skills', r.skills, function (e, x) { if (!x.level) e.level = 'intermediate'; });
        put('languages', r.languages, function (e, x) { if (!x.proficiency) e.proficiency = 'b1'; });
        put('certifications', r.certifications);
        put('projects', r.projects);
        /* what hh.ru keeps outside the sections: salary, employment, schedule… */
        var prefMap = { salary: /зарплат|salary|gehalt|salario|salário|gaji|급여|راتب/i, employment: /занятост|employment|beschäftigung/i, schedule: /график|schedule|arbeitszeit/i,
            relocation: /переезд|relocat|umzug/i, travel: /командиров|travel|reise/i, license: /прав[аи]|licen[cs]e|führerschein/i, citizenship: /гражданств|citizenship|staatsangeh/i };
        (r.extra || []).forEach(function (x) {
            var title = x.title || '';
            var key = Object.keys(prefMap).filter(function (k) { return prefMap[k].test(title); })[0];
            if (key && x.items && x.items.length) { m.prefs[key] = x.items.join(', '); return; }
            if (x.items && x.items.length) m.custom.push({ title: title, items: x.items.slice(0, 20) });
        });
        if (m.personal.dob) m.cultural.dob = true;
        return m;
    }

    function toast(msg, type) { if (window.__toast) window.__toast(msg, type || 'info'); }

    function mountListeners() { bindPaste(); bindFile(); }

    globalThis.RbIntake = { init: init, act: act, mountListeners: mountListeners, openPaste: openPaste, fromFile: fromFile, toModel: toModel, sample: sample };
})();
